import { memo, useState, useRef, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useApp } from '../context/useApp'
import * as api from '../api'
import { Home, Menu, X, ChevronDown, User, MapPin, Phone, Mail, LogOut, Building2, Shield, Bell } from 'lucide-react'

export default memo(function Navbar() {
  const { user, logout, setUser, transferRequests, properties } = useApp()
  const location = useLocation()
  const navigate = useNavigate()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [isImpersonating, setIsImpersonating] = useState(api.isImpersonating())
  const userMenuRef = useRef(null)

  const isActive = (path) => location.pathname === path

  useEffect(() => {
    function handleClickOutside(e) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setShowUserMenu(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const baseLinks = [
    { to: '/', label: 'Home' },
    { to: '/buyer', label: 'Risk Reports' },
    { to: '/pricing', label: 'Pricing' },
  ]

  const navLinks = user?.plan === 'homeowner'
    ? [
        { to: '/log', label: 'Home Log' },
        { to: '/property', label: 'My Property' },
        { to: '/find-contractors', label: 'Find a Pro' },
        { to: '/buyer', label: 'Search Reports' },
        { to: '/shop', label: 'Shop' },
      ]
    : user?.plan === 'buyer'
      ? [
          { to: '/buyer', label: 'Search Reports' },
          { to: '/find-contractors', label: 'Find a Pro' },
        ]
    : user?.plan === 'contractor'
      ? [
          { to: '/contractor', label: 'My Business' },
        ]
    : user?.plan === 'realtor' || user?.plan === 'realtorpro'
      ? [
          { to: '/buyer', label: 'Risk Reports' },
          { to: '/seller', label: 'Disclosure' },
          { to: '/showings', label: 'Showing Tools' },
        ]
      : user ? baseLinks : baseLinks

  const handleExitImpersonation = async () => {
    api.adminExitImpersonation()
    setIsImpersonating(false)
    const me = await api.getMe()
    setUser(me)
    navigate('/admin')
  }

  return (
    <>
    {/* Impersonation banner */}
    {isImpersonating && (
      <div className="flex items-center justify-between bg-risk-red/10 border-b border-risk-red/30 px-4 py-1.5 text-xs">
        <span className="text-risk-red font-medium flex items-center gap-1.5">
          <Shield className="w-3.5 h-3.5" /> Viewing as <strong>{user?.name}</strong> ({user?.email})
        </span>
        <button
          onClick={handleExitImpersonation}
          className="flex items-center gap-1 text-risk-red hover:text-red-300 bg-transparent border-0 cursor-pointer font-medium"
        >
          <X className="w-3 h-3" /> Exit
        </button>
      </div>
    )}
    <nav className="sticky top-0 z-50 bg-navy/95 backdrop-blur-sm border-b border-gold/15">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to={user?.plan === 'homeowner' ? '/log' : (user?.plan === 'buyer' || user?.plan === 'buyerpro') ? '/buyer' : user?.plan === 'contractor' ? '/contractor' : '/'} className="flex items-center gap-2.5 no-underline">
            <div className="w-8 h-8 bg-gold/20 border border-gold/30 rounded-lg flex items-center justify-center">
              <Home className="w-5 h-5 text-gold" />
            </div>
            <span className="font-serif text-xl font-bold text-cream tracking-wide">Heirloom</span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            {navLinks.map(link => (
              <Link
                key={link.to}
                to={link.to}
                aria-current={isActive(link.to) ? 'page' : undefined}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors no-underline flex items-center gap-1.5 ${
                  isActive(link.to)
                    ? 'bg-navy-lighter text-gold'
                    : 'text-parchment/60 hover:text-cream hover:bg-navy-lighter'
                }`}
              >
                {link.label}
                {link.badge && (
                  <span className="text-[10px] bg-gold/20 text-gold px-1.5 py-0.5 rounded-full font-bold">
                    {link.badge}
                  </span>
                )}
              </Link>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setShowUserMenu(prev => !prev)}
                  aria-label="Open account menu"
                  aria-expanded={showUserMenu}
                  aria-haspopup="true"
                  className="flex items-center gap-2 text-parchment/70 hover:text-cream bg-transparent border-0 cursor-pointer transition-colors"
                >
                  <span className="text-sm">{user.name}</span>
                  {transferRequests?.length > 0 && (
                    <span className="bg-red-500 text-white text-[10px] font-bold leading-none rounded-full px-1.5 py-0.5">
                      {transferRequests.length}
                    </span>
                  )}
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
                </button>

                {showUserMenu && (
                  <div className="absolute right-0 top-full mt-2 w-72 bg-navy-card border border-slate-border rounded-xl shadow-xl overflow-hidden z-50">
                    <div className="p-4 border-b border-slate-border">
                      <div className="mb-3">
                        <div className="text-cream font-semibold text-sm">{user.name}</div>
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                          (user.plan === 'realtor' || user.plan === 'realtorpro') ? 'bg-purple-500/20 text-purple-400'
                          : user.plan === 'contractor' ? 'bg-emerald-500/20 text-emerald-400'
                          : user.plan === 'buyer' ? 'bg-blue-400/20 text-blue-400'
                          : 'bg-gold/20 text-gold'
                        }`}>
                          {user.plan === 'realtorpro' ? 'REALTOR PRO' : user.plan === 'realtor' ? 'REALTOR' : user.plan === 'contractor' ? 'CONTRACTOR' : user.plan === 'buyer' ? 'HOME HUNTER' : 'HOMEOWNER'}
                        </span>
                      </div>
                      <div className="space-y-1.5 text-xs">
                        <div className="flex items-center gap-2 text-parchment/60">
                          <Mail className="w-3.5 h-3.5 text-sepia" />
                          {user.email}
                        </div>
                        {user.phone && (
                          <div className="flex items-center gap-2 text-parchment/60">
                            <Phone className="w-3.5 h-3.5 text-sepia" />
                            {user.phone}
                          </div>
                        )}
                        {properties?.some(p => p.propertyVerified) && (
                          <div className="flex items-center gap-2 text-parchment/60">
                            <MapPin className="w-3.5 h-3.5 text-sepia" />
                            {(() => { const p = properties.find(q => q.propertyVerified); return p ? [p.address, p.city, p.state, p.zip].filter(Boolean).join(', ') : '' })()}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="p-1">
                      <Link
                        to="/account"
                        onClick={() => setShowUserMenu(false)}
                        className="flex items-center gap-2 px-3 py-2 text-sm text-parchment/70 hover:bg-navy-lighter hover:text-cream rounded-lg no-underline transition-colors"
                      >
                        <User className="w-4 h-4" />
                        My Account
                      </Link>
                      {user?.isAdmin && (
                        <Link
                          to="/admin"
                          onClick={() => setShowUserMenu(false)}
                          className="flex items-center gap-2 px-3 py-2 text-sm text-gold/80 hover:bg-navy-lighter hover:text-gold rounded-lg no-underline transition-colors"
                        >
                          <Shield className="w-4 h-4" />
                          Admin Dashboard
                        </Link>
                      )}
                      <button
                        onClick={() => { logout(); setShowUserMenu(false); navigate('/'); }}
                        className="flex items-center gap-2 w-full px-3 py-2 text-sm text-sepia hover:bg-navy-lighter hover:text-parchment/70 rounded-lg bg-transparent border-0 cursor-pointer transition-colors"
                      >
                        <LogOut className="w-4 h-4" />
                        Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link to="/account" className="text-sm text-parchment/60 hover:text-cream no-underline">
                  Sign In
                </Link>
                <Link
                  to="/signup"
                  className="bg-gold hover:bg-gold-light text-white px-4 py-2 rounded-lg text-sm font-semibold no-underline transition-colors"
                >
                  Get Started Free
                </Link>
              </>
            )}
          </div>

          <button
            className="md:hidden text-parchment/60 hover:text-cream bg-transparent border-0 cursor-pointer transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={mobileOpen}
            aria-controls="mobile-nav"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {mobileOpen && (
          <div id="mobile-nav" className="md:hidden pb-4 border-t border-slate-border mt-2 pt-3">
            {navLinks.map(link => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setMobileOpen(false)}
                aria-current={isActive(link.to) ? 'page' : undefined}
                className={`block px-3 py-2 rounded-lg text-sm font-medium no-underline transition-colors ${
                  isActive(link.to) ? 'bg-navy-lighter text-gold' : 'text-parchment/60 hover:text-cream hover:bg-navy-lighter'
                }`}
              >
                {link.label} {link.badge && `(${link.badge})`}
              </Link>
            ))}
            <div className="mt-3 pt-3 border-t border-slate-border flex flex-col gap-2">
              {user ? (
                <>
                  <Link to="/account" onClick={() => setMobileOpen(false)} className="text-sm text-parchment/70 hover:text-cream no-underline px-3 py-2 rounded-lg hover:bg-navy-lighter transition-colors block">
                    My Account
                  </Link>
                  <button onClick={() => { logout(); setMobileOpen(false); navigate('/'); }} className="text-sm text-sepia hover:text-parchment/70 text-left px-3 py-2 rounded-lg hover:bg-navy-lighter bg-transparent border-0 cursor-pointer transition-colors w-full">
                    Sign Out
                  </button>
                </>
              ) : (
                <>
                <Link
                  to="/account"
                  onClick={() => setMobileOpen(false)}
                  className="text-sm text-parchment/60 no-underline px-3"
                >
                  Sign In
                </Link>
                <Link
                  to="/signup"
                  onClick={() => setMobileOpen(false)}
                  className="bg-gold text-white px-4 py-2 rounded-lg text-sm font-semibold no-underline text-center mx-3"
                >
                  Get Started Free
                </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
    </>
  )
})
